/** 菜单权限控制 */
import type { RouteLocationNormalized } from 'vue-router'
import type { MenuRoute } from '@/router/menuHelper/type.d'
import router from '@/router'

/** 以 path 为索引的一维菜单表 */
const menuMap = ref(new Map<string, MenuRoute>())
/** 依赖于已有menu，无独立菜单且通过route.meta.useModules校验可用的页面路径汇总 */
const menusFromModules = ref<Set<string>>(new Set())
/** 初始化标识 */
const inited = ref({
  /** 菜单是否完成初始化 */
  menu: false,
  /** 路由是否完成初始化，用于控制路由渲染时机 */
  route: false
})
/** 菜单表初始化 */
function init(menuList: MenuRoute[]) {
  menuMap.value = getMenuMap(menuList)
  menusFromModules.value = getMenuFromModules(menuMap.value)
  inited.value.menu = true
}
/**
 * 将菜单展开为以 path 为索引的一维表（递归）
 * @param menuItems 菜单列表
 */
function getMenuMap(menuItems: MenuRoute[]) {
  const output = menuItems.reduce((pre, item) => {
    const { route, child } = item
    pre.set(route.path, item)
    if (child) {
      getMenuMap(child).forEach((subItem, subPath) => pre.set(subPath, subItem))
    }
    return pre
  }, new Map<string, MenuRoute>())
  return output
}

/** 获取当前menuMap的模块名 */
function getMenuMapModules(): Set<string | undefined> {
  return new Set(
    [...menuMap.value.values()].map((item) => item.route?.meta?.module).filter((item) => !!item)
  )
}

/**
 * 根据route.meta.useModules和menuMap生成无对应服务端菜单的可用的页面路径汇总
 * ps: 不会生成对应的侧边栏菜单
 */
function getMenuFromModules(menuMap: Map<string, MenuRoute>): Set<string> {
  if (!menuMap) return new Set()
  const output = new Set<string>()
  try {
    const routeList = router.getRoutes()
    const menuModules = new Set(
      [...menuMap.values()].map((item) => item.route?.meta?.module).filter((item) => !!item)
    )
    routeList.forEach((item) => {
      const {
        path,
        meta: { useModules = [] }
      } = item
      const notExistingMenu = !menuMap.has(path)
      const isAvailableModule = useModules.some((m) => menuModules.has(m))
      if (notExistingMenu && isAvailableModule) {
        output.add(path)
      }
    })
  } catch (err) {
    console.error('[Fail]MenuFromModules初始化失败: ', err)
  }
  return output
}

/** 检查路由是否有权限 */
function checkRouteAvailable(route: RouteLocationNormalized) {
  return checkPathAvailable(route.path)
}
/** 检查路由路径是否有权限 */
function checkPathAvailable(path: string) {
  return menuMap.value.has(path) || menusFromModules.value.has(path)
}

/**
 * @param defaultPath 默认路由
 * 获取有权限的菜单项
 * 如为传入默认路由，则优先返回【创建供货单】
 * 其次返回菜单表第一项
 */
function getAvailableRoute(defaultPath = '/create-supply') {
  const indexPath = defaultPath || '/create-supply' // 初始页路径
  const map = menuMap.value
  let menu = map.get(indexPath)
  if (!menu) {
    menu = [...menuMap.value.values()].find((item) => {
      const redirect = item.route.redirect as string
      if (redirect) {
        // 多级redirect递归？
        return checkPathAvailable(redirect)
      }
      return true
    })
  }
  return (menu as MenuRoute).route
}
/** 标识路由完成初始化 */
function changeRouteInited() {
  inited.value.route = true
}
/** 重置状态 */
function reset() {
  menuMap.value.clear()
  menusFromModules.value.clear()
  inited.value.menu = false
  inited.value.route = false
}

export function useMenu() {
  return {
    isMenuInited: () => inited.value.menu,
    isRouteInited: () => inited.value.route,
    changeRouteInited,
    init,
    checkRouteAvailable,
    checkPathAvailable,
    getAvailableRoute,
    getMenuMapModules,
    reset
  }
}

export default useMenu
